import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from '@angular/material/dialog';

import { FsBadgeModule } from '@firestitch/badge';
import { FsDialogModule } from '@firestitch/dialog';
import { FilterConfig, FsFilterModule, ItemType } from '@firestitch/filter';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EnvironmentData } from 'app/core';

import { SidenavService } from '@libs/layouts/side-nav/services';

import { Environment } from '@app/common/interfaces';


@Component({
  templateUrl: './environment-switch.component.html',
  styleUrls: ['./environment-switch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FsDialogModule,
    CdkScrollable,
    MatDialogContent,
    FsFilterModule,
    FsBadgeModule,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class EnvironmentSwitchComponent {

  public environments = signal<Environment[]>([]);
  public keyword = signal<string>('');
  public filteredEnvironments = computed(() => {
    const keyword = this.keyword().trim().toLowerCase();

    if (!keyword) {
      return this.environments();
    }

    return this.environments()
      .filter((environment: Environment) => {
        return String(environment.name ?? '').toLowerCase().includes(keyword);
      });
  });

  public filterConfig: FilterConfig = {
    chips: false,
    queryParam: false,
    autofocus: true,
    items: [
      {
        name: 'keyword',
        type: ItemType.Keyword,
        label: 'Search',
        fullWidth: true,
      },
    ],
    change: (query: { keyword?: string }) => {
      this.keyword.set(query.keyword ?? '');
    },
  };

  private _dialogRef = inject<MatDialogRef<EnvironmentSwitchComponent>>(MatDialogRef);
  private _environmentData = inject(EnvironmentData);
  private _router = inject(Router);
  private _sidenavService = inject(SidenavService);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    this._init();
  }

  public switch(environment: Environment): void {
    this._environmentData.switch(environment.id)
      .subscribe(
        (response) => {
          this._sidenavService.hide();
          this._dialogRef.close(response);
          this._router.navigate(['blank']).then(() => {
            this._router.navigateByUrl('/');
          });
        });
  }

  private _init(): void {
    this._environmentData.gets()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((environments: Environment[]) => {
        this.environments.set(environments);
      });
  }

}
